import { useState, useRef, useEffect } from 'react';
import Calendar from './Calendar';
import styles from './DateInput.module.css';
import Portal from './Portal';
import { CalendarIcon } from '@spotoninc/nexus-web-assets';

/**
 * DateInput component with custom calendar popup
 *
 * @param {Object} props - Component props
 * @param {string} props.id - Input ID
 * @param {string} props.label - Label text
 * @param {string} props.value - Current selected date value (controlled)
 * @param {Function} props.onChange - onChange handler
 * @param {Object} props.error - Error object from react-hook-form
 * @param {string} props.value - Current selected date value (controlled)
 * @param {string} props.defaultValue - Default date value
 * @param {string} props.placeholder - Placeholder text
 * @param {string} props.className - Additional CSS classes
 * @param {boolean} props.disabled - Whether the input is disabled
 */
function DateInput({
  id,
  label,
  value,
  onChange,
  error,
  defaultValue,
  placeholder = 'MM/DD/YYYY',
  className = '',
  disabled = false,
  ...rest
}) {
  // Helper function to parse date string
  const parseDate = dateString => {
    if (!dateString) return null;
    const [month, day, year] = dateString.split('/');
    return new Date(year, month - 1, day);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [tempSelectedDate, setTempSelectedDate] = useState(null); // Temporary selection in calendar

  // Initialize currentMonth based on selected date or today's date
  const getInitialMonth = () => {
    if (value) {
      const parsedDate = parseDate(value);
      return parsedDate || new Date();
    }
    return new Date(); // Default to today's date
  };

  const [currentMonth, setCurrentMonth] = useState(getInitialMonth());

  // Update currentMonth when value changes
  useEffect(() => {
    if (value) {
      const parsedDate = parseDate(value);
      if (parsedDate) {
        setCurrentMonth(
          new Date(parsedDate.getFullYear(), parsedDate.getMonth(), 1)
        );
      }
    }
  }, [value]);

  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const calendarRef = useRef(null);

  const handleToggle = () => {
    if (!disabled) {
      if (!isOpen) {
        // Set currentMonth to selected date's month or today's month
        const selectedDate = parseDate(value);
        if (selectedDate) {
          setCurrentMonth(
            new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1)
          );
          setTempSelectedDate(selectedDate);
        } else {
          // Default to today's date if no date is selected
          const today = new Date();
          setCurrentMonth(new Date(today.getFullYear(), today.getMonth(), 1));
          setTempSelectedDate(today);
        }
      }
      setIsOpen(!isOpen);
    }
  };

  const handleDateSelect = date => {
    // Only set temporary selection, don't close calendar or update form yet
    setTempSelectedDate(date);
  };

  const handleSave = () => {
    if (tempSelectedDate) {
      const formattedDate = formatDate(tempSelectedDate);
      setIsOpen(false);

      // Call onChange if provided
      if (onChange) {
        onChange(formattedDate);
      }
    }
  };

  const handleCancel = () => {
    setTempSelectedDate(null);
    setIsOpen(false);
  };

  const formatDate = date => {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const getDisplayValue = () => {
    return value || '';
  };

  return (
    <div className={`${styles.container} ${className}`} ref={containerRef}>
      {label && (
        <label htmlFor={id} className={styles.label}>
          {label}
        </label>
      )}

      <div
        className={`${styles.dateinput} ${isOpen ? styles.open : ''} ${error ? styles.error : ''} ${disabled ? styles.disabled : ''}`}
        ref={triggerRef}
      >
        <input
          id={id}
          type="text"
          className={styles.field}
          value={getDisplayValue()}
          placeholder={placeholder}
          disabled={disabled}
          readOnly
          onClick={handleToggle}
          {...rest}
        />

        <button
          type="button"
          className={styles.iconButton}
          onClick={handleToggle}
          disabled={disabled}
          aria-label="Open calendar"
        >
          <CalendarIcon width="24px" height="24px" />
        </button>
      </div>

      {error && <span className={styles.errorMessage}>{error?.message}</span>}

      {isOpen && (
        <Portal
          isOpen={isOpen}
          triggerRef={triggerRef}
          onClose={handleCancel}
          zIndex={10001}
        >
          <div ref={calendarRef} style={{ width: '100%' }}>
            <Calendar
              currentMonth={currentMonth}
              onMonthChange={setCurrentMonth}
              selectedDate={parseDate(value)}
              tempSelectedDate={tempSelectedDate}
              onDateSelect={handleDateSelect}
              onCancel={handleCancel}
              onSave={handleSave}
            />
          </div>
        </Portal>
      )}
    </div>
  );
}

export default DateInput;
